import React, { useEffect, useRef } from 'react';
import { $convertFromMarkdownString } from '@lexical/markdown';
import {
  LexicalComposer,
  InitialConfigType,
} from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { LexicalEditor, ParagraphNode } from 'lexical';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';
import {
  LexicalStyle,
  theme,
} from '../../SubjectViewer/Modules/AITools/ImpressionEditor/MarkdownEditor/MarkdownEditor';
import { ListBreakPlugin } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/MarkdownEditor/plugins/ListBreakPlugin';
import { MarkdownTablePlugin } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/MarkdownEditor/plugins/MarkdownTablePlugin';
import { NestedListPlugin } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/MarkdownEditor/plugins/NestedListPlugin';
import { DEFAULT_TRANSFORMERS } from './transformers';

const VIEWER_NODES = [
  ParagraphNode,
  HeadingNode,
  QuoteNode,
  HorizontalRuleNode,
  ListNode,
  ListItemNode,
  LinkNode,
  CodeNode,
  CodeHighlightNode,
  TableNode,
  TableRowNode,
  TableCellNode,
];

/**
 * Keeps editor state in sync when markdown or syncKey changes (initial render is
 * satisfied by LexicalComposer initialConfig).
 */
const MarkdownSyncPlugin: React.FC<{
  markdown: string;
  syncKey: string;
}> = ({ markdown, syncKey }) => {
  const [editor] = useLexicalComposerContext();
  const prev = useRef({ markdown, syncKey });

  useEffect(() => {
    if (
      prev.current.markdown === markdown &&
      prev.current.syncKey === syncKey
    ) {
      return;
    }
    prev.current = { markdown, syncKey };
    editor.update(() => {
      $convertFromMarkdownString(markdown, DEFAULT_TRANSFORMERS);
    });
  }, [editor, markdown, syncKey]);

  return null;
};

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  content: {
    width: '100%',
    outline: 'none',
  },
}));

export interface LexicalReadOnlyMarkdownViewerProps {
  markdown: string;
  /** e.g. rule id — forces reload when switching entities with identical markdown */
  syncKey: string;
  className?: string;
  namespace?: string;
  onError?: (error: Error, editor: LexicalEditor) => void;
}

const LexicalReadOnlyMarkdownViewerInner: React.FC<{
  markdown: string;
  syncKey: string;
  className?: string;
}> = ({ markdown, syncKey, className }) => {
  const styles = useStyles();
  return (
    <>
      <LexicalStyle />
      <MarkdownSyncPlugin markdown={markdown} syncKey={syncKey} />
      <div className={styles.container}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable className={clsx(styles.content, className)} />
          }
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={null}
        />
      </div>
      <ListBreakPlugin />
      <NestedListPlugin />
      <TablePlugin />
      <MarkdownTablePlugin />
    </>
  );
};

/**
 * Read-only markdown using the same Lexical stack as template / impression editors
 * (including GFM-style pipe tables via {@link MarkdownTablePlugin}).
 */
const LexicalReadOnlyMarkdownViewer: React.FC<
  LexicalReadOnlyMarkdownViewerProps
> = ({
  markdown,
  syncKey,
  className,
  namespace = 'read-only-markdown-viewer',
  onError = err => console.error(err),
}) => {
  const initialConfig: InitialConfigType = {
    namespace,
    onError,
    nodes: VIEWER_NODES,
    editorState: () =>
      $convertFromMarkdownString(markdown, DEFAULT_TRANSFORMERS),
    editable: false,
    theme,
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <LexicalReadOnlyMarkdownViewerInner
        markdown={markdown}
        syncKey={syncKey}
        className={className}
      />
    </LexicalComposer>
  );
};

export default LexicalReadOnlyMarkdownViewer;
